import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import { ContentWrapper } from './TripPlanDetailDrawer.styles'
import { useCallback, useContext, useMemo, useState, type MouseEvent } from 'react'
import { useTripPlanDetailDrawerCopy } from './useTripPlanDetailDrawerCopy'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import { useGetPlaceOverview } from 'tripPlanner/hooks/api/chat'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Places from 'constants/places'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { usePlaceByName } from 'hooks/usePlace'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { DrawerExperienceSearchTile } from './ExperienceListingDrawer/ExperienceDetailView'
import ExperienceDetailView from './ExperienceListingDrawer/ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './ExperienceListingDrawer/mapExperienceDetailToDrawer'
import ThingsToDoTab from './ThingsToDoTab'
import OverviewTab from './OverviewTab'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import DrawerAskAIFooter from 'tripPlanner/components/DrawerAskAI/DrawerAskAIFooter'
import ModalContext from 'contexts/ModalContext'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useExperiences } from 'tripPlanner/hooks/api/offer'
import useExperienceDetail from '../hooks/useExperienceDetail'
import useOfferReviews from '../hooks/useOfferReviews'
import usePlaceThingsToDo from '../hooks/usePlaceThingsToDo'
import useTripPlannerOfferListTracking from '../hooks/useTripPlannerOfferListTracking'

interface Props {
  isOpen: boolean
  onDismiss: () => void
  onAfterClose?: () => void
  googlePlaceId: string
  placeName: string
  countryName?: string
  stopId?: number
  onDeleteStop?: (stopId: number) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  chatId?: number
}

type DetailDrawerTab = 'overview' | 'things-to-do'

function TripPlanDetailDrawer({
  isOpen,
  onDismiss,
  onAfterClose,
  googlePlaceId,
  placeName,
  countryName,
  stopId,
  onDeleteStop,
  itineraryData,
  onSendMessage,
  chatId,
}: Props) {
  const labels = useTripPlanDetailDrawerCopy(countryName)
  const showModal = useContext(ModalContext)
  const eventBus = useTripPlannerEventBus()

  const {
    data: overview,
    isLoading: isOverviewLoading,
  } = useGetPlaceOverview(googlePlaceId, chatId)

  const currentCurrency = useContext(CurrencyContext)
  const geo = useContext(GeoContext)
  const [activeTab, setActiveTab] = useState<DetailDrawerTab>('overview')
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)

  const {
    experiences: nearbyExperiences,
    experiencesCount,
    countLabel: experiencesCountLabel,
    formattedLowestPrice,
    isLoading: isNearbyExperiencesLoading,
    isError: isNearbyExperiencesError,
  } = usePlaceThingsToDo({
    placeName,
    countryName,
    enabled: isOpen,
  })

  const thingsToDoSummary = useMemo(() => ({
    count: experiencesCount,
    countLabel: experiencesCountLabel,
    formattedLowestPrice,
    isLoading: isNearbyExperiencesLoading,
    isError: isNearbyExperiencesError,
  }), [experiencesCount, experiencesCountLabel, formattedLowestPrice, isNearbyExperiencesLoading, isNearbyExperiencesError])

  const experienceOfferIds = useMemo(() => nearbyExperiences.map((exp) => exp.offerId), [nearbyExperiences])
  const trackingOffers = useExperiences({
    currentCurrency,
    experienceIds: isOpen ? experienceOfferIds : [],
    currentRegionCode: geo.currentRegionCode,
  })
  const trackingOfferById = useMemo(() => {
    return new Map(trackingOffers.data.map((offer) => [offer.id, offer]))
  }, [trackingOffers.data])
  const getTrackingOffer = useCallback((offerId: string) => {
    return trackingOfferById.get(offerId)
  }, [trackingOfferById])

  const searchQuery = useMemo(() => {
    return countryName ? `${placeName}, ${countryName}` : placeName
  }, [countryName, placeName])
  const tracking = useTripPlannerOfferListTracking('experienceThingsToDoTab', searchQuery)

  const {
    experienceDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail,
  } = useExperienceDetail({
    offerId: selectedOfferId,
    currencyCode: currentCurrency,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!experienceDetail,
  })

  const detailExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  const isDetailView = !!selectedOfferId

  const [resolvedExperienceSearchUrl] = useExperienceSearchUrl({
    latLong: {
      latitude: overview?.lat,
      longitude: overview?.lng,
    },
    placeLevels: ['multi_city_vicinity', 'province_state', 'city'],
  })
  const resolvedPlaceByName = usePlaceByName(placeName)

  const browseAllResultsUrl = useMemo(() => {
    if (resolvedPlaceByName?.id && resolvedPlaceByName.id !== Places.Anywhere.id) {
      return buildUrl(
        `/search/experiences?${replaceExperienceQueryString({
          destinationId: resolvedPlaceByName.id,
          destinationName: resolvedPlaceByName.name,
        })}`,
        geo.currentRegionCode,
      )
    }

    const searchParams = new URLSearchParams(resolvedExperienceSearchUrl.split('?')[1] ?? '')
    const destinationId = searchParams.get('destinationId')
    if (destinationId && destinationId !== Places.Anywhere.id) {
      return buildUrl(resolvedExperienceSearchUrl, geo.currentRegionCode)
    }

    return buildUrl('/experiences', geo.currentRegionCode)
  }, [geo.currentRegionCode, resolvedExperienceSearchUrl, resolvedPlaceByName])

  const handleDeleteStop = useCallback(async() => {
    if (stopId !== undefined && onDeleteStop) {
      const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
      if (!confirmed) {
        return
      }
      onDeleteStop(stopId)
      onDismiss()
    }
  }, [stopId, onDeleteStop, onDismiss, showModal, placeName])

  const showDeleteOption = stopId !== undefined && onDeleteStop !== undefined
  const handleTabClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const nextTab = event.currentTarget.dataset.tab
    if (nextTab === 'overview' || nextTab === 'things-to-do') {
      setActiveTab(nextTab)
    }
  }, [])
  const handleThingsToDoCardClick = useCallback(() => {
    setActiveTab('things-to-do')
  }, [])
  const handleSelectExperience = useCallback((experience: DrawerExperienceSearchTile) => {
    setSelectedOfferId(experience.offerId)
  }, [])
  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
  }, [])
  const handleRetryDetail = useCallback(() => {
    refetchDetail()
  }, [refetchDetail])
  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    onDismiss()
  }, [onDismiss])
  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    onAfterClose?.()
  }, [onAfterClose])

  const handleShowExperienceOnMap = useCallback(() => {
    if (detailExperience?.geoLocation) {
      handleClose()
      eventBus.dispatch({
        type: 'show_experience_on_map',
        data: {
          searchQuery: detailExperience.name,
          offers: [{
            id: detailExperience.offerId,
            name: detailExperience.name,
            coordinates: detailExperience.geoLocation,
          }],
        },
      })
    }
  }, [detailExperience, eventBus, handleClose])

  const headerActions = useMemo(() => {
    if (!isDetailView || !detailExperience) return undefined

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {detailExperience.geoLocation && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowExperienceOnMap}
          >
            {labels.showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={detailExperience.name}
            offerId={detailExperience.offerId}
            offerType="experience"
            coordinates={detailExperience.geoLocation}
            variant="outline"
          />
        )}
      </Group>
    )
  }, [detailExperience, handleShowExperienceOnMap, labels.showMapLabel, isDetailView, itineraryData, onSendMessage])

  const askAIFooter = (
    <DrawerAskAIFooter
      contextLabel={placeName}
      isOpen={isOpen}
    />
  )

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      onBack={isDetailView ? handleBack : undefined}
      direction="right-to-left"
      onDelete={!isDetailView && showDeleteOption ? handleDeleteStop : undefined}
      footer={askAIFooter}
      headerActions={headerActions}
    >
      <Group direction="vertical" horizontalAlign="center">
        {isDetailView && (
          <>
            {isDetailLoading && !detailExperience && (
              <ContentWrapper direction="vertical" gap={16}>
                <LoadingBox height={200} borderRadius="M" style={{ width: '100%' }} />
                <LoadingBox height={28} width={240} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </ContentWrapper>
            )}
            {isDetailError && !isDetailLoading && !detailExperience && (
              <ContentWrapper direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
                <BodyText variant="medium" colour="critical">
                  {labels.failedToLoadExperienceDetailText}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={handleRetryDetail}>
                  {labels.tryAgainLabel}
                </TextButton>
              </ContentWrapper>
            )}
            {detailExperience && (
              <ExperienceDetailView
                experience={detailExperience}
                reviews={reviews}
                reviewsLoading={reviewsLoading}
              />
            )}
          </>
        )}

        {!isDetailView && <ContentWrapper direction="vertical" gap={24}>
          {/* Header */}
          <Group direction="vertical" gap={4}>
            {isOverviewLoading && (
              <>
                <LoadingBox height={36} width={280} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </>
            )}
            {!isOverviewLoading && (
              <>
                <Heading variant="heading2">{overview?.locationName ?? placeName}</Heading>
                {countryName && countryName !== placeName && <BodyText variant="medium">{labels.cityInCountryText}</BodyText>}
              </>
            )}
          </Group>
          <TabButtonsGroup>
            <TabButton
              data-tab="overview"
              size="small"
              isActive={activeTab === 'overview'}
              onClick={handleTabClick}
            >
              {labels.overviewTabLabel}
            </TabButton>
            <TabButton
              data-tab="things-to-do"
              size="small"
              isActive={activeTab === 'things-to-do'}
              onClick={handleTabClick}
            >
              {labels.thingsToDoTabLabel}
            </TabButton>
          </TabButtonsGroup>

          {activeTab === 'overview' && (
            <OverviewTab
              googlePlaceId={googlePlaceId}
              placeName={placeName}
              countryName={countryName}
              onSwitchToThingsToDo={handleThingsToDoCardClick}
              thingsToDoSummary={thingsToDoSummary}
              chatId={chatId}
            />
          )}

          {activeTab === 'things-to-do' && (
            <OfferListEventsProvider tracking={tracking}>
              <ThingsToDoTab
                experiences={nearbyExperiences}
                isLoading={isNearbyExperiencesLoading}
                onSelectExperience={handleSelectExperience}
                placeName={placeName}
                browseAllResultsUrl={browseAllResultsUrl}
                itineraryData={itineraryData}
                onSendMessage={onSendMessage}
                getTrackingOffer={getTrackingOffer}
              />
            </OfferListEventsProvider>
          )}

        </ContentWrapper>}
      </Group>
    </TripDrawer>
  )
}

export default TripPlanDetailDrawer
